/**
 * AppShell - T041, T146, T147
 *
 * Main application layout with responsive sidebar navigation
 * - Desktop: Fixed sidebar
 * - Mobile: Collapsible drawer with hamburger menu
 */
import { type ReactNode, useState, useEffect, useRef } from 'react';
import { Link, useMatchRoute, useLocation } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth, useLogout } from '../../hooks/useAuth';
import { useSession } from '../../hooks/useTenantContext';
import { ChangelogBell } from '../profile/ChangelogBell';
import { CommandPalette, LoadingSpinner } from '../ui';
import { MobileBottomNav } from './MobileBottomNav';
import { NotificationDropdown } from '../notifications/NotificationDropdown';
import { getBuildInfo } from '@armard/shared';

const buildInfo = getBuildInfo({
  channel: import.meta.env.VITE_BUILD_CHANNEL,
  revision: import.meta.env.VITE_BUILD_REVISION,
  buildNumber: import.meta.env.VITE_BUILD_NUMBER,
});

interface AppShellProps {
  children: ReactNode;
}

interface NavItem {
  label: string;
  path: string;
  icon: ReactNode;
}

interface NavGroup {
  /** Section caption; omitted for the top-level (Dashboard) group. */
  label?: string;
  items: NavItem[];
}

const navItems: NavItem[] = [
  {
    label: 'Dashboard',
    path: '/dashboard',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
        />
      </svg>
    ),
  },
  {
    label: 'Kunden',
    path: '/customers',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
    ),
  },
  {
    label: 'Rechnungen',
    path: '/invoices',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    ),
  },
  {
    label: 'Mahnungen',
    path: '/reminders',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
        />
      </svg>
    ),
  },
  {
    label: 'Angebote',
    path: '/estimates',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 14l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
        />
      </svg>
    ),
  },
  {
    label: 'Verträge',
    path: '/contracts',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"
        />
      </svg>
    ),
  },
  {
    label: 'Ausgaben',
    path: '/expenses',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
        />
      </svg>
    ),
  },
  {
    label: 'Lieferanten',
    path: '/suppliers',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M3 7l9-4 9 4M4 10v8a2 2 0 002 2h12a2 2 0 002-2v-8M10 14h4"
        />
      </svg>
    ),
  },
  {
    label: 'Cashflow',
    path: '/cashflow',
    icon: <span className="w-5 h-5 text-center">€</span>,
  },
  {
    label: 'Abschreibungen',
    path: '/depreciations',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
        />
      </svg>
    ),
  },
  {
    label: 'Export',
    path: '/expenses/export',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
        />
      </svg>
    ),
  },
  {
    label: 'Audit-Log',
    path: '/audit-log',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
        />
      </svg>
    ),
  },
  {
    label: 'Einstellungen',
    path: '/settings',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
    ),
  },
];

// Sprint 006 A7 — Sidebar-IA: Dashboard top-level, dann Verkauf / Einkauf /
// System. Angebote war zuvor gar nicht in der Sidebar (nur per ⌘K erreichbar).
const NAV_GROUPS: NavGroup[] = [
  { items: navItems.filter((i) => i.path === '/dashboard') },
  {
    label: 'Verkauf',
    items: ['/customers', '/invoices', '/estimates', '/contracts', '/reminders'].map(
      (p) => navItems.find((i) => i.path === p)!
    ),
  },
  {
    label: 'Einkauf',
    items: ['/expenses', '/suppliers', '/cashflow', '/depreciations', '/expenses/export'].map(
      (p) => navItems.find((i) => i.path === p)!
    ),
  },
  {
    label: 'System',
    items: ['/audit-log', '/settings'].map((p) => navItems.find((i) => i.path === p)!),
  },
];

function NavLink({ item, onClick }: { item: NavItem; onClick?: () => void }) {
  const matchRoute = useMatchRoute();
  const isActive =
    item.path === '/dashboard'
      ? matchRoute({ to: '/dashboard' }) !== false || matchRoute({ to: '/' }) !== false
      : matchRoute({ to: item.path, fuzzy: true }) !== false;

  return (
    <Link
      to={item.path}
      onClick={onClick}
      className={`
        flex items-center gap-3 px-5 py-3.5 rounded-lg text-sm font-medium
        transition-colors duration-200
        ${isActive ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'}
      `}
    >
      <span className={isActive ? 'text-white' : 'text-white/40'}>{item.icon}</span>
      {item.label}
    </Link>
  );
}

// Stbr gets a reduced sidebar: only Ausgaben + Export visible (FR-038).
const STBR_PATHS = new Set(['/expenses', '/expenses/export', '/cashflow']);
const ADMIN_PATHS = new Set([
  '/dashboard',
  '/customers',
  '/invoices',
  '/estimates',
  '/contracts',
  '/reminders',
  '/audit-log',
  '/settings',
]);

// Sidebar content shared between desktop and mobile
function SidebarContent({
  onNavClick,
  isMobile = false,
}: {
  onNavClick?: () => void;
  isMobile?: boolean;
}) {
  const { user } = useAuth();
  const logout = useLogout();
  const { data: sessionData } = useSession();
  const activeRole = sessionData?.activeRole ?? null;
  const activeTenant = sessionData?.availableTenants.find(
    (tenant) => tenant.tenantId === sessionData.activeTenantId
  );
  const visibleGroups = NAV_GROUPS.map((g) => ({
    ...g,
    items:
      activeRole === 'steuerberater'
        ? g.items.filter((i) => STBR_PATHS.has(i.path))
        : activeRole === 'admin'
          ? g.items.filter((i) => ADMIN_PATHS.has(i.path))
          : g.items,
  })).filter((g) => g.items.length > 0);

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-4 border-b border-white/[0.06]">
        <Link to="/dashboard" onClick={onNavClick} className="flex items-center gap-3">
          <div className="w-[48px] h-[48px] flex items-center justify-center border border-white/10 rounded-lg shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="13" viewBox="0 0 42 22">
              <path
                fill="currentColor"
                className="text-white/80"
                d="M10.825,0.825 C17.418,0.825 24.012,0.825 30.605,0.825 C30.605,4.121 30.605,7.419 30.605,10.715 C24.012,10.715 17.418,10.715 10.825,10.715 C10.825,7.419 10.825,4.121 10.825,0.825 ZM0.935,10.715 C4.231,10.715 7.529,10.715 10.825,10.715 C10.825,14.011 10.825,17.309 10.825,20.605 C7.529,20.605 4.231,20.605 0.935,20.605 C0.935,17.309 0.935,14.011 0.935,10.715 ZM30.605,10.715 C33.901,10.715 37.199,10.715 40.495,10.715 C40.495,14.011 40.495,17.309 40.495,20.605 C37.199,20.605 33.901,20.605 30.605,20.605 C30.605,17.309 30.605,14.011 30.605,10.715 Z"
              />
            </svg>
          </div>
          <div className="flex flex-col">
            <span className="text-xl font-bold text-white tracking-tight">Armard</span>
            <span className="text-xs text-white/30">CRM</span>
          </div>
        </Link>
      </div>

      {/* Notifications bell + role pill — only on desktop to keep mobile clean */}
      {!isMobile && (
        <div className="px-5 py-2 flex items-center justify-between border-b border-white/[0.04]">
          {activeRole && (
            <span
              className={`text-xs px-2 py-0.5 rounded border ${
                activeRole === 'steuerberater'
                  ? 'bg-blue-500/10 border-blue-500/30 text-blue-100'
                  : activeRole === 'admin'
                    ? 'bg-amber-500/10 border-amber-500/30 text-amber-100'
                    : 'bg-white/5 border-white/10 text-white/80'
              }`}
            >
              {activeRole === 'steuerberater'
                ? 'Steuerberater'
                : activeRole === 'geschäftsführer'
                  ? 'Geschäftsführer'
                  : 'Admin'}
            </span>
          )}
          <NotificationDropdown />
        </div>
      )}

      {sessionData?.activeTenantId && (
        <div className="px-5 py-2 border-b border-white/[0.04]">
          <p className="text-[10px] uppercase tracking-wider text-white/35">Mandant</p>
          {sessionData.availableTenants.length > 1 ? (
            <Link
              to="/tenant-picker"
              onClick={onNavClick}
              className="mt-0.5 flex items-center justify-between gap-2 text-sm text-white/80 hover:text-white"
              aria-label={`Aktiver Mandant ${activeTenant?.name ?? 'unbekannt'}; Mandant wechseln`}
            >
              <span className="truncate">{activeTenant?.name ?? 'Aktiver Mandant'}</span>
              <span className="shrink-0 text-xs text-yellow-400">Wechseln</span>
            </Link>
          ) : (
            <p className="mt-0.5 truncate text-sm text-white/80">
              {activeTenant?.name ?? 'Aktiver Mandant'}
            </p>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-5 space-y-1 overflow-y-auto">
        {visibleGroups.map((group, gi) => (
          <div key={group.label ?? 'top'} className={gi > 0 ? 'pt-3' : ''}>
            {group.label && (
              <p className="px-5 pb-1.5 text-[11px] font-medium uppercase tracking-wider text-white/30">
                {group.label}
              </p>
            )}
            <div className="space-y-1">
              {group.items.map((item) => (
                <NavLink key={item.path} item={item} onClick={onNavClick} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Version & Links */}
      <div className="px-5 py-3 border-t border-white/[0.06] text-white/30 text-xs leading-relaxed">
        <p className="break-all" title={`Revision: ${buildInfo.revision}`}>
          v{buildInfo.version}
        </p>
        <a
          href="https://armadaquadrat.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="block hover:text-white/50 transition-colors !min-h-0"
        >
          armadaquadrat.com
        </a>
        <a
          href="https://armadaquadrat.com/impressum"
          target="_blank"
          rel="noopener noreferrer"
          className="block hover:text-white/50 transition-colors !min-h-0"
        >
          Impressum
        </a>
      </div>

      {/* Command palette trigger */}
      <div className="px-5 py-2 border-t border-white/[0.06]">
        {isMobile ? (
          <button
            onClick={() => {
              // Dispatch keyboard event to open command palette
              document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
              onNavClick?.();
            }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/30 rounded-lg text-yellow-400 text-sm font-medium transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            Befehlspalette
          </button>
        ) : (
          <button
            onClick={() => {
              document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
            }}
            className="text-xs text-yellow-400/70 hover:text-yellow-400 transition-colors cursor-pointer"
          >
            <kbd className="px-1.5 py-0.5 bg-yellow-500/10 rounded text-yellow-400">⌘K</kbd>
            <span className="ml-1">Befehlspalette</span>
          </button>
        )}
      </div>

      {/* User section */}
      <div
        className="p-3 border-t border-white/[0.06]"
        style={
          isMobile ? { paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 12px)' } : undefined
        }
      >
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
            <span className="text-sm text-white/70">
              {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || '?'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white truncate">{user?.name || user?.email}</p>
          </div>
          {/* T550 (FR-UI-003): Changelog-Bell sits between the user-name
              and the logout button so it's immediately visible without
              taking sidebar real-estate. Badge auto-clears on first open. */}
          <ChangelogBell />
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
            aria-label="Abmelden"
            data-testid="logout-button"
            className="p-1.5 text-white/40 hover:text-white transition-colors"
            title="Abmelden"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
          </motion.button>
        </div>
        {logout.isError && (
          <p role="alert" className="px-3 pb-2 text-xs text-red-300">
            Abmelden fehlgeschlagen. Bitte erneut versuchen.
          </p>
        )}
      </div>
    </div>
  );
}

export function AppShell({ children }: AppShellProps) {
  const { isLoading } = useAuth();
  const { data: sessionData } = useSession();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // A0 (Sprint 006): scrolling lives in <main>, not the body — the shell
  // (sidebar, BG animation, mobile chrome) must stand still while content
  // scrolls. Body scroll would also let macOS rubber-banding drag the whole
  // shell. Because the browser no longer resets scroll on navigation
  // (that only happens for the window scroller), we reset it ourselves.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, [location.pathname]);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="h-dvh overflow-hidden">
      {/* Command Palette */}
      <CommandPalette />

      {/* Mobile Header - T146 */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 h-16 bg-black/80 backdrop-blur-xl border-b border-white/[0.06] flex items-center justify-between px-4">
        {/* Logo left */}
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 flex items-center justify-center border border-white/10 rounded-lg">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="8" viewBox="0 0 42 22">
              <path
                fill="currentColor"
                className="text-white/80"
                d="M10.825,0.825 C17.418,0.825 24.012,0.825 30.605,0.825 C30.605,4.121 30.605,7.419 30.605,10.715 C24.012,10.715 17.418,10.715 10.825,10.715 C10.825,7.419 10.825,4.121 10.825,0.825 ZM0.935,10.715 C4.231,10.715 7.529,10.715 10.825,10.715 C10.825,14.011 10.825,17.309 10.825,20.605 C7.529,20.605 4.231,20.605 0.935,20.605 C0.935,17.309 0.935,14.011 0.935,10.715 ZM30.605,10.715 C33.901,10.715 37.199,10.715 40.495,10.715 C40.495,14.011 40.495,17.309 40.495,20.605 C37.199,20.605 33.901,20.605 30.605,20.605 C30.605,17.309 30.605,14.011 30.605,10.715 Z"
              />
            </svg>
          </div>
          <span className="text-lg font-bold text-white tracking-tight">Armard</span>
        </div>
        {sessionData?.activeTenantId && (
          <div className="min-w-0 flex-1 px-2">
            {sessionData.availableTenants.length > 1 ? (
              <Link
                to="/tenant-picker"
                className="block truncate text-center text-xs text-white/70 hover:text-white"
                aria-label={`Aktiver Mandant ${sessionData.availableTenants.find((t) => t.tenantId === sessionData.activeTenantId)?.name ?? 'unbekannt'}; Mandant wechseln`}
              >
                {sessionData.availableTenants.find((t) => t.tenantId === sessionData.activeTenantId)
                  ?.name ?? 'Mandant wechseln'}{' '}
                ▾
              </Link>
            ) : (
              <span className="block truncate text-center text-xs text-white/60">
                {sessionData.availableTenants.find((t) => t.tenantId === sessionData.activeTenantId)
                  ?.name ?? 'Mandant'}
              </span>
            )}
          </div>
        )}
        {/* Right side: Command + Hamburger */}
        <div className="flex items-center gap-1">
          {/* Command palette button */}
          <button
            onClick={() => {
              document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
            }}
            className="w-10 h-10 flex items-center justify-center text-yellow-400 hover:text-yellow-300 !min-w-0 transition-colors"
            aria-label="Befehlspalette"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </button>
          {/* Hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="w-10 h-10 flex items-center justify-center text-white/70 hover:text-white !min-w-0"
            aria-label="Menu öffnen"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay - T147 */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="lg:hidden fixed inset-0 z-[45] bg-black/60 backdrop-blur-sm"
            />

            {/* Drawer */}
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="lg:hidden fixed left-0 top-0 bottom-0 z-50 w-80 max-w-[85vw] bg-black/95 backdrop-blur-xl border-r border-white/[0.06]"
            >
              {/* Close button */}
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="absolute top-4 right-4 p-2 text-white/40 hover:text-white"
                aria-label="Menu schließen"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              <SidebarContent onNavClick={() => setIsMobileMenuOpen(false)} isMobile />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:block fixed left-0 top-0 bottom-0 w-80 border-r border-white/[0.06] bg-black/60 backdrop-blur-lg">
        <SidebarContent />
      </aside>

      {/* Main content — the app's single scroll container (A0) */}
      <main
        ref={mainRef}
        className="lg:ml-80 min-w-0 h-full overflow-y-auto overflow-x-hidden overscroll-contain pt-16 lg:pt-0"
      >
        {/* Measured navigation height includes the safe-area inset. */}
        <div className="px-6 py-8 lg:px-10 lg:py-10 w-full pb-[calc(var(--mobile-nav-height,80px)+24px)] lg:pb-10">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation - T148 */}
      {!isMobileMenuOpen && <MobileBottomNav />}
    </div>
  );
}

export default AppShell;
